import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import DataTable from "@/components/Datatable/Datatable";
import { Column } from "@/components/Datatable/types";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/ui/button";
import { BillingInvoice } from "../../api/getBillingInvoices/types";
import BillingSummaryCard from "../BillingSummaryCard/BillingSummaryCard";
import BillingInvoiceDrawer from "../BillingInvoiceDrawer/BillingInvoiceDrawer";
import { useBillingSubscriptionInvoicesPage } from "../../hooks/useBillingSubscriptionInvoicesPage";
import {
  invoiceActionsColumn,
  invoiceAmountColumn,
  invoiceBalanceColumn,
  invoiceDateColumn,
  invoiceNumberColumn,
  invoiceStatusColumn,
} from "../../config/billingInvoiceColumns";

const BillingSubscriptionInvoicesPage: React.FC = () => {
  const {
    loading,
    title,
    nextInvoice,
    paymentMethod,
    sorting,
    handleSortingChange,
    pageRows,
    totalCount,
    pageIndex,
    pageSize,
    handlePageChange,
    invoiceDrawer,
    invoicePdfDownload,
  } = useBillingSubscriptionInvoicesPage();

  const columns = useMemo<Column<BillingInvoice>[]>(
    () => [
      invoiceNumberColumn(invoiceDrawer.openInvoice),
      invoiceDateColumn,
      invoiceAmountColumn,
      invoiceBalanceColumn,
      invoiceStatusColumn,
      invoiceActionsColumn({
        onViewInvoice: invoiceDrawer.openInvoice,
        onDownloadPdf: invoicePdfDownload.downloadInvoicePdf,
        downloadingInvoiceId: invoicePdfDownload.downloadingInvoiceId,
      }),
    ],
    [
      invoiceDrawer.openInvoice,
      invoicePdfDownload.downloadInvoicePdf,
      invoicePdfDownload.downloadingInvoiceId,
    ],
  );

  return (
    <MainPageWrapper title={title}>
      <Button asChild className="mb-4 rounded-md" size="sm" variant="ghost">
        <Link to="/hub/billing/subscriptions">
          <ArrowLeft className="h-4 w-4" />
          Back to subscriptions
        </Link>
      </Button>
      {(loading || (nextInvoice && paymentMethod)) && (
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <BillingSummaryCard
            detail={nextInvoice?.detail}
            label="Next invoice"
            loading={loading}
            value={nextInvoice?.value}
          />
          <BillingSummaryCard
            detail={paymentMethod?.detail}
            label="Payment method"
            loading={loading}
            value={paymentMethod?.value}
          />
        </div>
      )}
      <DataTable<BillingInvoice>
        columns={columns}
        data={pageRows}
        loading={loading}
        noDataMessage="No invoices yet"
        onPageChange={handlePageChange}
        onSortingChange={handleSortingChange}
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowKeyExtractor={(invoice) => invoice.id}
        sorting={sorting}
        totalCount={totalCount}
      />
      <BillingInvoiceDrawer
        downloadingInvoiceId={invoicePdfDownload.downloadingInvoiceId}
        invoice={invoiceDrawer.invoice}
        loading={invoiceDrawer.loading}
        onClose={invoiceDrawer.closeInvoice}
        onDownloadPdf={invoicePdfDownload.downloadInvoicePdf}
        open={invoiceDrawer.isOpen}
      />
    </MainPageWrapper>
  );
};

export default BillingSubscriptionInvoicesPage;
